/**
 * Utilitários para manipulação e validação de datas no formato DD/MM/YYYY
 */

import {
  differenceInYears,
  format,
  intervalToDuration,
  isValid as isValidDateFns,
  parse,
  parseISO,
  startOfDay,
  subDays,
  subMonths,
  subYears,
} from "date-fns";
import { ptBR } from "date-fns/locale";
import { formatInTimeZone } from "date-fns-tz";

export type PetAgeUnit = "years" | "months" | "days";

const ISO_DATE_ONLY_REGEX = /^\d{4}-\d{2}-\d{2}$/;
const BRAZIL_TIME_ZONE = "America/Sao_Paulo";

/** Retorna a data civil atual da clínica no fuso horário do Brasil. */
export function getBrazilDate(date = new Date()): string {
  return formatInTimeZone(date, BRAZIL_TIME_ZONE, "yyyy-MM-dd");
}

/**
 * Converte Date para "YYYY-MM-DD" usando o fuso local (sem shift de UTC).
 */
export function toISODateOnlyLocal(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/**
 * Converte uma Date para ISO mantendo a data local selecionada às 12:00.
 */
export function toLocalNoonISOString(date: Date): string {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 12, 0, 0, 0).toISOString();
}

/**
 * Faz parse de "YYYY-MM-DD" como data local (00:00 no fuso do navegador).
 */
export function parseISODateOnlyLocal(value: string): Date | null {
  if (!ISO_DATE_ONLY_REGEX.test(value)) return null;
  const parsed = parse(value, "yyyy-MM-dd", new Date());
  return isValidDateFns(parsed) ? parsed : null;
}

/**
 * Faz parse de uma string de data/datetime ISO de forma segura:
 * - "YYYY-MM-DD" (data-only) é tratada como data pura (sem shift de fuso)
 * - ISO datetime (com horário/offset) é parseada normalmente
 */
export function parseISODateOrDateTime(value?: string | null): Date | null {
  if (!value) return null;

  // Date-only (YYYY-MM-DD): usar parse em timezone local para evitar shift.
  if (ISO_DATE_ONLY_REGEX.test(value)) {
    return parseISODateOnlyLocal(value);
  }

  // Datetime ISO: parse padrão
  const parsed = parseISO(value);
  return isValidDateFns(parsed) ? parsed : null;
}

/**
 * Formata data (dd/MM/yyyy).
 * - Para "YYYY-MM-DD" retorna diretamente a mesma data (sem considerar fuso).
 */
export function formatDateBR(value?: string | null): string {
  if (!value) return "-";
  if (ISO_DATE_ONLY_REGEX.test(value)) {
    const display = formatISOToDisplay(value);
    return display || "-";
  }
  const parsed = parseISODateOrDateTime(value);
  if (!parsed) return "-";
  return format(parsed, "dd/MM/yyyy", { locale: ptBR });
}

/**
 * Formata data+hora (dd/MM/yyyy HH:mm) no fuso do navegador.
 */
export function formatDateTimeBR(value?: string | null): string {
  if (!value) return "-";
  const parsed = parseISODateOrDateTime(value);
  if (!parsed) return "-";
  return format(parsed, "dd/MM/yyyy HH:mm", { locale: ptBR });
}

/**
 * Valida se uma data no formato DD/MM/YYYY é válida
 * @param dateString - Data no formato DD/MM/YYYY
 * @returns true se a data for válida, false caso contrário
 */
export function isValidDate(dateString: string): boolean {
    if (!dateString || dateString.length !== 10) return false;

    const [day, month, year] = dateString.split('/');

    // Verifica se todos os componentes existem e têm o tamanho correto
    if (!day || !month || !year || day.length !== 2 || month.length !== 2 || year.length !== 4) {
        return false;
    }

    const dayNum = parseInt(day, 10);
    const monthNum = parseInt(month, 10);
    const yearNum = parseInt(year, 10);

    // Verifica se são números válidos
    if (isNaN(dayNum) || isNaN(monthNum) || isNaN(yearNum)) {
        return false;
    }

    // Verifica limites básicos
    if (dayNum < 1 || dayNum > 31) return false;
    if (monthNum < 1 || monthNum > 12) return false;
    if (yearNum < 1900 || yearNum > 2100) return false;

    // Verifica se a data existe no calendário
    const date = new Date(yearNum, monthNum - 1, dayNum);
    return (
        date.getDate() === dayNum &&
        date.getMonth() === monthNum - 1 &&
        date.getFullYear() === yearNum
    );
}

/**
 * Aplica máscara de data DD/MM/YYYY com validação em tempo real
 * @param value - Valor digitado pelo usuário
 * @returns Valor formatado com máscara
 */
export function maskDateInput(value: string): string {
    // Remove todos os caracteres não numéricos
    const digits = value.replace(/\D/g, "").slice(0, 8);

    const parts: string[] = [];

    // Dia (primeiros 2 dígitos)
    if (digits.length > 0) {
        const day = digits.slice(0, Math.min(2, digits.length));
        // Valida se o dia não passa de 31
        if (day.length === 2) {
            const dayNum = parseInt(day, 10);
            if (dayNum > 31) {
                parts.push("31");
            } else {
                parts.push(day);
            }
        } else {
            parts.push(day);
        }
    }

    // Mês (próximos 2 dígitos)
    if (digits.length >= 3) {
        const month = digits.slice(2, Math.min(4, digits.length));
        // Valida se o mês não passa de 12
        if (month.length === 2) {
            const monthNum = parseInt(month, 10);
            if (monthNum > 12) {
                parts.push("12");
            } else {
                parts.push(month);
            }
        } else {
            parts.push(month);
        }
    }

    // Ano (últimos 4 dígitos)
    if (digits.length >= 5) {
        parts.push(digits.slice(4, 8));
    }

    return parts.join("/").slice(0, 10);
}

/**
 * Converte data do formato DD/MM/YYYY para ISO (YYYY-MM-DD)
 * @param displayDate - Data no formato DD/MM/YYYY
 * @returns Data no formato ISO ou string vazia se inválida
 */
export function convertDisplayToISO(displayDate: string): string {
    if (!isValidDate(displayDate)) return "";

    const [day, month, year] = displayDate.split("/");
    return `${year}-${month}-${day}`;
}

/**
 * Converte data do formato ISO (YYYY-MM-DD) para DD/MM/YYYY
 * @param isoDate - Data no formato ISO
 * @returns Data no formato DD/MM/YYYY ou string vazia se inválida
 */
export function formatISOToDisplay(isoDate?: string | null): string {
    if (!isoDate) return "";

    const [year, month, day] = isoDate.split("-");
    if (!year || !month || !day) return "";

    return `${day.padStart(2, "0")}/${month.padStart(2, "0")}/${year}`;
}

/**
 * Valida se uma data não é futura (para datas de nascimento)
 * @param dateString - Data no formato DD/MM/YYYY
 * @returns true se a data não for futura, false caso contrário
 */
export function isNotFutureDate(dateString: string): boolean {
    if (!isValidDate(dateString)) return false;

    const [day, month, year] = dateString.split("/");
    const inputDate = new Date(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10));
    const today = new Date();
    today.setHours(23, 59, 59, 999); // Fim do dia atual

    return inputDate <= today;
}

/**
 * Obtém mensagem de erro para data inválida
 * @param dateString - Data no formato DD/MM/YYYY
 * @returns Mensagem de erro ou string vazia se válida
 */
export function getDateErrorMessage(dateString: string): string {
  if (!dateString) return "";

  if (dateString.length < 10) {
    return "";
  }

  if (!isValidDate(dateString)) {
    return "Data inválida";
  }

  return "";
}

export function getPetAgeValueFromBirthDate(
  date: Date | string | null | undefined,
): { value: string; unit: PetAgeUnit } | null {
  if (!date) return null;

  const birthDate = typeof date === "string" ? parseISODateOrDateTime(date) : date;
  if (!birthDate || Number.isNaN(birthDate.getTime())) return null;

  const now = startOfDay(new Date());
  const normalizedBirthDate = startOfDay(birthDate);

  const years = differenceInYears(now, normalizedBirthDate);
  if (years >= 1) {
    return { value: String(years), unit: "years" };
  }

  const { months, days } = intervalToDuration({
    start: normalizedBirthDate,
    end: now,
  });

  if ((months ?? 0) >= 1) {
    return { value: String(months), unit: "months" };
  }

  return { value: String(days ?? 0), unit: "days" };
}

export function getBirthDateFromPetAge(value: string, unit: PetAgeUnit): string {
  const normalized = value.replace(/\D/g, "").trim();
  if (!normalized) return "";

  const numericValue = Number.parseInt(normalized, 10);
  if (!Number.isFinite(numericValue) || numericValue < 0) return "";

  const today = startOfDay(new Date());

  const birthDate =
    unit === "years"
      ? subYears(today, numericValue)
      : unit === "months"
        ? subMonths(today, numericValue)
        : subDays(today, numericValue);

  return toISODateOnlyLocal(birthDate);
}

/**
 * Formata a idade de um pet a partir da data de nascimento
 * @param date - Data de nascimento (Date ou string ISO)
 * @returns String formatada (ex: "2 anos", "5 meses", "10 dias") ou null se data inválida
 */
export function formatPetAge(date: Date | string | null | undefined): string | null {
  if (!date) return null;

  const birthDate = typeof date === "string" ? parseISODateOrDateTime(date) : date;
  if (!birthDate) return null;

  // Validar se a data é válida
  if (Number.isNaN(birthDate.getTime())) return null;

  const now = new Date();
  const years = differenceInYears(now, birthDate);

  if (years >= 1) {
    return `${years} ${years === 1 ? "ano" : "anos"}`;
  }

  // Menos de 1 ano → usar meses
  const { months, days } = intervalToDuration({
    start: birthDate,
    end: now,
  });

  if ((months ?? 0) >= 1) {
    return `${months} ${months === 1 ? "mês" : "meses"}`;
  }

  // Fallback em dias para filhotes
  return `${days ?? 0} ${(days ?? 0) === 1 ? "dia" : "dias"}`;
}

/** Local time as `HH:mm` (for `<input type="time" />`). */
export function formatLocalTimeHHmm(date: Date = new Date()): string {
  return format(date, "HH:mm");
}
